import { useCallback, useState, type FormEvent } from 'react';

import { fullReleaseWaitlist } from '~/marketing/landingContent';
import { captureEvent, presenceEvents } from '../../presence/posthogPresence';
import {
  fullReleaseWaitlistEnabled,
  submitFullReleaseWaitlist,
} from '../../waitlist/fullReleaseWaitlist';
import './landingRef.css';

type Props = {
  source: string;
  /** A city page knows the metro; the homepage capture leaves it to the waitlist form. */
  citySlug?: string;
  inputId?: string;
  className?: string;
};

export function HeroEmailCapture({
  source,
  citySlug,
  inputId = 'hero-waitlist-email',
  className,
}: Props) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const enabled = fullReleaseWaitlistEnabled();

  const onSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!enabled || status === 'loading') return;
      setStatus('loading');
      setMessage('');
      const result = await submitFullReleaseWaitlist({ email, source, citySlug });
      if (!result.ok) {
        setStatus('error');
        setMessage(result.message);
        return;
      }
      const { data } = result;
      captureEvent(presenceEvents.waitlist_full_release_submit, {
        created: data.created,
        already_registered: data.alreadyRegistered,
        source,
      });
      setStatus('success');
      setMessage(
        data.alreadyRegistered ? fullReleaseWaitlist.successAgain : fullReleaseWaitlist.successNew
      );
    },
    [email, enabled, source, citySlug, status]
  );

  if (!enabled) {
    return (
      <p className="hero-email-capture__disabled ml-text-body-secondary" role="status">
        {fullReleaseWaitlist.disabledHint}
      </p>
    );
  }

  if (status === 'success') {
    return (
      <p className="hero-email-capture__success" role="status">
        {message}
      </p>
    );
  }

  return (
    <form
      className={'hero-email-capture' + (className ? ` ${className}` : '')}
      onSubmit={onSubmit}
      noValidate>
      <label className="visually-hidden" htmlFor={inputId}>
        {fullReleaseWaitlist.emailLabel}
      </label>
      <input
        id={inputId}
        className="hero-email-capture__input"
        type="email"
        name="email"
        autoComplete="email"
        placeholder="Your email"
        required
        value={email}
        onChange={(ev) => setEmail(ev.target.value)}
      />
      <button
        type="submit"
        className="ml-btn ml-btn--primary eb-cta eb-cta--primary hero-email-capture__submit"
        disabled={status === 'loading'}
        aria-busy={status === 'loading'}>
        {status === 'loading' ? (
          <span className="ml-waitlistForm__spinner" role="status" aria-label="Joining waitlist" />
        ) : (
          fullReleaseWaitlist.submitLabel
        )}
      </button>
      {status === 'error' ? (
        <p className="hero-email-capture__err" role="alert">
          {message || fullReleaseWaitlist.errorGeneric}
        </p>
      ) : null}
    </form>
  );
}
